import React, { useState } from 'react';
import { X, Gift, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { createComp, resendInvite } from './api';

export default function AddCompClientModal({ onClose, onDone, theme }) {
  const dark = theme === 'dark';
  const [form, setForm] = useState({ company_name: '', contact_email: '', contact_phone: '' });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null); // { result, comp?|match? }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    // Email is the dedup key and the field that identifies the comp recipient,
    // so it's required; company name is required by the Client record itself.
    if (!form.contact_email.trim()) { toast.error('Contact email required'); return; }
    if (!form.company_name.trim()) { toast.error('Company name required'); return; }
    setBusy(true);
    try {
      const res = await createComp(form);
      if (res.result === 'created') { toast.success('Comp client created & invited'); onDone(); return; }
      setOutcome(res);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const compMatch = async () => {
    setBusy(true);
    try {
      const m = outcome.match;
      const res = await createComp({ source_type: m.kind === 'candidate' ? 'candidate' : 'client', source_id: m.id });
      toast.success(res.result === 'already_comped' ? 'Already comped' : 'Existing client comped');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await resendInvite('client', outcome.comp.source_id);
      toast.success('Invitation resent');
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const card = dark ? 'bg-gray-900 text-gray-100 border-gray-700' : 'bg-white text-gray-900 border-gray-200';
  const input = `w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog" aria-modal="true" aria-labelledby="add-comp-client-title" onClick={onClose}>
      <div className={`w-full max-w-md rounded-2xl border shadow-xl p-5 ${card}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 id="add-comp-client-title" className="text-lg font-semibold">Add comp client</h3>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {!outcome ? (
          <div className="space-y-3">
            <input className={input} type="email" placeholder="Contact email *" value={form.contact_email} onChange={set('contact_email')} />
            <input className={input} placeholder="Company name *" value={form.company_name} onChange={set('company_name')} />
            <input className={input} placeholder="Contact phone (optional)" value={form.contact_phone} onChange={set('contact_phone')} />
            <button disabled={busy} onClick={submit}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">
              <Gift size={16} /> {busy ? 'Checking…' : 'Comp this client'}
            </button>
            <p className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>We’ll match on the contact email to avoid duplicate comps.</p>
          </div>
        ) : outcome.result === 'already_comped' ? (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 text-sm ${dark ? 'border-amber-500/30 bg-amber-500/10' : 'border-amber-200 bg-amber-50'}`}>
              <b>{outcome.comp.company_name}</b> already has a comp ({outcome.comp.status}).
            </div>
            <button disabled={busy} onClick={resend}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">
              <RefreshCw size={16} /> Resend invitation email
            </button>
            <button onClick={onClose} className="w-full text-sm text-gray-500 hover:underline">Cancel</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 text-sm ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
              Found an existing {outcome.match.kind === 'candidate' ? 'File-Review candidate' : 'client'}: <b>{outcome.match.company_name}</b>. Comp that one instead of creating a duplicate?
            </div>
            <button disabled={busy} onClick={compMatch}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">
              <Gift size={16} /> Comp the existing one
            </button>
            <button onClick={onClose} className="w-full text-sm text-gray-500 hover:underline">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
